/**
 * Service Repository (Refactored)
 *
 * This is a facade that provides backward compatibility while delegating
 * to specialized modules:
 * - ServiceQueryRepository: Read operations
 * - ServiceCommandRepository: Write operations
 * - ServiceWorkflowService: Business logic & workflows
 *
 * This refactoring improves maintainability by separating concerns and
 * reducing file size from 418 lines to ~160 lines per module.
 */

import { BaseRepository, RepositoryResult, PaginatedResult, QueryOptions, PaginationParams } from './BaseRepository';
import { Service } from '@/shared/types';

// Import specialized modules
import { serviceQueryRepository } from './ServiceQueryRepository';
import { serviceCommandRepository } from './ServiceCommandRepository';
import { serviceWorkflowService } from './ServiceWorkflowService';
import type { ServiceDependencies } from './ServiceWorkflowService';

/**
 * Service Repository (Facade)
 *
 * Provides a unified interface to service operations while delegating
 * to specialized modules. This maintains backward compatibility with
 * existing code while improving code organization.
 *
 * @deprecated Prefer using the specialized modules directly:
 * - Use serviceQueryRepository for read operations
 * - Use serviceCommandRepository for write operations
 * - Use serviceWorkflowService for business logic
 */
export class ServiceRepository extends BaseRepository<Service> {
  protected tableName = 'services' as const;

  // ============================================================================
  // QUERY OPERATIONS (delegated to ServiceQueryRepository)
  // ============================================================================

  /**
   * Find all services for a user with pagination and filtering
   */
  async findAll(
    userId: string,
    options?: QueryOptions & PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Service>>> {
    return serviceQueryRepository.findAll(userId, options);
  }

  /**
   * Find a single service by ID
   */
  async findById(id: string): Promise<RepositoryResult<Service>> {
    return serviceQueryRepository.findById(id);
  }

  /**
   * Find services by user ID (alias for findAll with default options)
   */
  async findByUserId(userId: string): Promise<RepositoryResult<Service[]>> {
    return serviceQueryRepository.findByUserId(userId);
  }

  /**
   * Search services by name or description
   */
  async search(
    userId: string,
    query: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Service>>> {
    return serviceQueryRepository.search(userId, query, options);
  }

  /**
   * Get total count of services for a user
   */
  async count(userId: string): Promise<RepositoryResult<number>> {
    return serviceQueryRepository.count(userId);
  }

  // ============================================================================
  // COMMAND OPERATIONS (delegated to ServiceCommandRepository)
  // ============================================================================

  /**
   * Create a new service
   */
  async create(userId: string, serviceData: Omit<Service, 'id' | 'userId'>): Promise<RepositoryResult<Service>> {
    return serviceCommandRepository.create(userId, serviceData);
  }

  /**
   * Update an existing service
   */
  async update(id: string, serviceData: Partial<Service>): Promise<RepositoryResult<Service>> {
    return serviceCommandRepository.update(id, serviceData);
  }

  /**
   * Delete a service
   */
  async delete(id: string): Promise<RepositoryResult<void>> {
    return serviceCommandRepository.delete(id);
  }

  /**
   * Batch create services
   */
  async createBatch(
    userId: string,
    servicesData: Omit<Service, 'id' | 'userId'>[]
  ): Promise<RepositoryResult<Service[]>> {
    return serviceCommandRepository.createBatch(userId, servicesData);
  }

  // ============================================================================
  // WORKFLOW OPERATIONS (delegated to ServiceWorkflowService)
  // ============================================================================

  /**
   * Check if a service has dependencies (jobs, quotes)
   */
  async hasDependencies(id: string): Promise<RepositoryResult<ServiceDependencies>> {
    return serviceWorkflowService.hasDependencies(id);
  }

  // ============================================================================
  // RE-EXPORT SPECIALIZED MODULES FOR DIRECT USE
  // ============================================================================

  /**
   * Service Query Repository
   * Use this for read-only operations on services
   */
  static Query = serviceQueryRepository;

  /**
   * Service Command Repository
   * Use this for write operations on services
   */
  static Command = serviceCommandRepository;

  /**
   * Service Workflow Service
   * Use this for business logic and workflow operations
   */
  static Workflow = serviceWorkflowService;
}

// Export singleton instance for backward compatibility
export const serviceRepository = new ServiceRepository();

// ============================================================================
// RE-EXPORT SPECIALIZED MODULES FOR DIRECT USE
// ============================================================================

/**
 * Service Query Repository
 * Use this for read-only operations on services
 */
export { serviceQueryRepository };

/**
 * Service Command Repository
 * Use this for write operations on services
 */
export { serviceCommandRepository };

/**
 * Service Workflow Service
 * Use this for business logic and workflow operations
 */
export {
  serviceWorkflowService,
  ServiceDependencies,
  // Also export additional types from ServiceWorkflowService
  type ServiceUsageStats,
};

// Import types from ServiceWorkflowService for re-export
import type { ServiceUsageStats } from './ServiceWorkflowService';
